import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, X, Power, ArrowUp, ArrowDown, Image as ImageIcon, Lock, ExternalLink, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

const inputCls = "w-full bg-[#050505] border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#666] focus:border-white/40 focus:outline-none transition-colors duration-300";
const emptySection = () => ({ heading: "", text: "", image_path: "" });
const emptyForm = () => ({ label: "", title: "", intro: "", sections: [emptySection()], button_label: "", button_link: "" });

function SectionImage({ path, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/nav/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
      toast.success("Foto hochgeladen.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Foto-Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (path) {
    return (
      <div className="relative rounded-xl overflow-hidden border border-white/10 w-full sm:w-56 h-36 shrink-0">
        <img src={`/api/nav/files/${path}`} alt="" className="w-full h-full object-cover" />
        <button
          type="button"
          onClick={() => onChange("")}
          data-testid="section-image-remove"
          className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center hover:bg-red-500/80 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <label
      data-testid="section-image-upload"
      className="flex flex-col items-center justify-center gap-2 w-full sm:w-56 h-36 shrink-0 rounded-xl border border-dashed border-white/15 text-[#888] hover:border-white/35 hover:text-white cursor-pointer transition-colors"
    >
      {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImageIcon size={20} />}
      <span className="text-xs">{uploading ? "Lädt hoch…" : "Foto hochladen"}</span>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={upload} disabled={uploading} />
    </label>
  );
}

function NavEditorForm({ initial, onClose, onSaved }) {
  const isNew = !initial;
  const [form, setForm] = useState(() =>
    initial
      ? {
          label: initial.label,
          title: initial.content?.title || "",
          intro: initial.content?.intro || "",
          sections: initial.content?.sections?.length ? initial.content.sections.map((s) => ({ ...s })) : [emptySection()],
          button_label: initial.content?.button_label || "",
          button_link: initial.content?.button_link || "",
        }
      : emptyForm()
  );
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setSection = (i, field, value) => setForm((f) => ({ ...f, sections: f.sections.map((s, idx) => (idx === i ? { ...s, [field]: value } : s)) }));
  const addSection = () => setForm((f) => ({ ...f, sections: [...f.sections, emptySection()] }));
  const removeSection = (i) => setForm((f) => ({ ...f, sections: f.sections.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (isNew && !form.label.trim()) { toast.error("Bitte einen Tab-Namen eingeben."); return; }
    if (!form.title.trim()) { toast.error("Bitte eine Überschrift für die Seite eingeben."); return; }
    setSaving(true);
    const content = {
      title: form.title.trim(),
      intro: form.intro.trim(),
      sections: form.sections
        .filter((s) => s.heading.trim() || s.text.trim() || s.image_path)
        .map((s) => ({ heading: s.heading.trim(), text: s.text.trim(), image_path: s.image_path })),
      button_label: form.button_label.trim(),
      button_link: form.button_link.trim(),
    };
    try {
      let data;
      if (isNew) {
        ({ data } = await api.post("/admin/nav", { label: form.label.trim(), content }));
        toast.success(`Tab "${data.label}" erstellt – jetzt im Menü sichtbar unter /${data.key}.`);
      } else {
        ({ data } = await api.put(`/admin/nav/${initial.id}`, { label: form.label.trim(), content }));
        toast.success("Tab gespeichert.");
      }
      onSaved(data, isNew);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} data-testid="nav-editor-form" className="rounded-2xl border border-white/10 bg-[#0a0a0a] p-5 sm:p-6 mb-8 space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-xl sm:text-2xl text-white">{isNew ? "Neuer Tab" : `"${initial.label}" bearbeiten`}</h3>
        <button type="button" onClick={onClose} data-testid="nav-editor-close" className="text-[#888] hover:text-white"><X size={18} /></button>
      </div>

      <div>
        <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Tab-Name (im Menü)</label>
        <input data-testid="nav-label-input" value={form.label} onChange={set("label")} placeholder="z.B. Garantie" className={inputCls} />
        {!isNew && <p className="text-xs text-[#666] mt-1.5">Seiten-Adresse bleibt: /{initial.key}</p>}
      </div>

      <div>
        <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Überschrift der Seite</label>
        <input data-testid="nav-title-input" value={form.title} onChange={set("title")} placeholder="z.B. Unsere Garantie" className={inputCls} />
      </div>

      <div>
        <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Einleitungstext (optional)</label>
        <textarea data-testid="nav-intro-input" rows={3} value={form.intro} onChange={set("intro")} placeholder="Kurzer Text direkt unter der Überschrift" className={`${inputCls} resize-y`} />
      </div>

      <div>
        <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-3">Textabschnitte</label>
        <div className="space-y-4">
          {form.sections.map((s, i) => (
            <div key={i} data-testid={`nav-section-${i}`} className="rounded-xl border border-white/10 p-4 flex flex-col sm:flex-row gap-4">
              <SectionImage path={s.image_path} onChange={(p) => setSection(i, "image_path", p)} />
              <div className="flex-1 space-y-2">
                <input data-testid={`nav-section-heading-${i}`} value={s.heading} onChange={(e) => setSection(i, "heading", e.target.value)} placeholder="Überschrift des Abschnitts" className={inputCls} />
                <textarea data-testid={`nav-section-text-${i}`} rows={3} value={s.text} onChange={(e) => setSection(i, "text", e.target.value)} placeholder="Text des Abschnitts" className={`${inputCls} resize-y`} />
                {form.sections.length > 1 && (
                  <button type="button" onClick={() => removeSection(i)} data-testid={`nav-section-remove-${i}`} className="inline-flex items-center gap-1.5 text-xs text-[#888] hover:text-red-400 transition-colors">
                    <Trash2 size={12} /> Abschnitt entfernen
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={addSection} data-testid="nav-add-section" className="mt-3 inline-flex items-center gap-1.5 text-sm text-[#aaa] hover:text-white transition-colors">
          <Plus size={14} /> Abschnitt hinzufügen
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Button-Text (optional)</label>
          <input data-testid="nav-button-label-input" value={form.button_label} onChange={set("button_label")} placeholder="z.B. Jetzt beauftragen" className={inputCls} />
        </div>
        <div>
          <label className="block text-xs font-mono uppercase tracking-widest text-[#888] mb-2">Button-Ziel (Link)</label>
          <input data-testid="nav-button-link-input" value={form.button_link} onChange={set("button_link")} placeholder="/beauftragen oder https://…" className={inputCls} />
        </div>
      </div>

      <button type="submit" disabled={saving} data-testid="nav-save-btn" className="rounded-full bg-white text-[#050505] px-6 py-3 text-sm font-medium hover:bg-[#d1d5db] transition-colors disabled:opacity-50">
        {saving ? "Speichert…" : "Speichern"}
      </button>
    </form>
  );
}

export default function NavTabsView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = closed, "new" = create, item obj = edit

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/nav");
      setItems(data);
    } catch (e) { toast.error("Tabs konnten nicht geladen werden."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleVisible = async (item) => {
    try {
      const { data } = await api.put(`/admin/nav/${item.id}`, { visible: !item.visible });
      setItems((p) => p.map((x) => (x.id === item.id ? data : x)));
      toast.success(data.visible ? `"${item.label}" ist jetzt sichtbar.` : `"${item.label}" ist jetzt komplett gesperrt.`);
    } catch (e) { toast.error("Aktualisierung fehlgeschlagen."); }
  };

  const move = async (item, direction) => {
    try {
      await api.patch(`/admin/nav/${item.id}/move`, { direction });
      await load();
    } catch (e) { toast.error("Verschieben fehlgeschlagen."); }
  };

  const remove = async (item) => {
    try {
      await api.delete(`/admin/nav/${item.id}`);
      setItems((p) => p.filter((x) => x.id !== item.id));
      toast.success(`Tab "${item.label}" gelöscht.`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Löschen fehlgeschlagen."); }
  };

  return (
    <div data-testid="nav-tabs-view">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-3">
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl">Tabs</h1>
        <button onClick={() => setEditing("new")} data-testid="nav-new-btn" className="inline-flex items-center gap-2 rounded-full bg-white text-[#050505] px-5 py-3 text-sm font-medium hover:bg-[#d1d5db] transition-colors">
          <Plus size={16} /> Neuer Tab
        </button>
      </div>
      <p className="text-sm text-[#888] mb-8 max-w-2xl">
        Bestimme, welche Menü-Punkte auf der Website sichtbar sind, erstelle neue Seiten mit eigenen Texten, Fotos und Buttons, und lege mit den Pfeilen die Reihenfolge im Menü fest.
        Bei Standard-Tabs (Schloss-Symbol) kannst du nur die Sichtbarkeit ändern — ausgeblendet heißt komplett gesperrt, auch über einen direkten Link nicht mehr erreichbar.
      </p>

      {editing && (
        <NavEditorForm
          initial={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(data, isNew) => setItems((p) => (isNew ? [...p, data] : p.map((x) => (x.id === data.id ? data : x))))}
        />
      )}

      {loading ? (
        <p className="text-[#888] font-mono text-sm py-20 text-center">Lädt…</p>
      ) : (
        <div className="space-y-3" data-testid="nav-items-list">
          {items.map((item, i) => (
            <div key={item.id} data-testid={`nav-item-${item.id}`} className={`rounded-2xl border p-4 sm:p-5 flex items-center gap-3 sm:gap-4 flex-wrap ${item.visible ? "border-white/10 bg-[#0a0a0a]" : "border-red-500/20 bg-[#0a0a0a]/60 opacity-70"}`}>
              <div className="flex flex-col shrink-0">
                <button type="button" onClick={() => move(item, "up")} disabled={i === 0} data-testid={`nav-move-up-${item.id}`} className="text-[#666] hover:text-white transition-colors disabled:opacity-20"><ArrowUp size={14} /></button>
                <button type="button" onClick={() => move(item, "down")} disabled={i === items.length - 1} data-testid={`nav-move-down-${item.id}`} className="text-[#666] hover:text-white transition-colors disabled:opacity-20"><ArrowDown size={14} /></button>
              </div>

              <div className="flex-1 min-w-[140px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-medium">{item.label}</p>
                  {item.type === "builtin" && <Lock size={12} className="text-[#666]" data-testid={`nav-builtin-badge-${item.id}`} />}
                  <span data-testid={`nav-status-${item.id}`} className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${item.visible ? "bg-green-500/20 text-green-300 border border-green-500/30" : "bg-red-500/20 text-red-300 border border-red-500/30"}`}>
                    {item.visible ? "Sichtbar" : "Gesperrt"}
                  </span>
                </div>
                <a href={item.route} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-[#666] hover:text-white font-mono mt-1 transition-colors">
                  {item.route} <ExternalLink size={10} />
                </a>
              </div>

              <div className="flex items-center gap-2 ml-auto">
                <button onClick={() => toggleVisible(item)} data-testid={`nav-toggle-${item.id}`} title={item.visible ? "Sperren" : "Sichtbar machen"}
                  className={`w-9 h-9 rounded-lg border flex items-center justify-center transition-colors ${item.visible ? "border-white/10 text-[#888] hover:text-red-400 hover:border-red-400/40" : "border-white/10 text-[#888] hover:text-green-400 hover:border-green-400/40"}`}>
                  <Power size={16} />
                </button>
                {item.type === "custom" && (
                  <>
                    <button onClick={() => setEditing(item)} data-testid={`nav-edit-${item.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-white hover:border-white/40 transition-colors"><Pencil size={16} /></button>
                    <button onClick={() => remove(item)} data-testid={`nav-delete-${item.id}`} className="w-9 h-9 rounded-lg border border-white/10 flex items-center justify-center text-[#888] hover:text-red-400 hover:border-red-400/40 transition-colors"><Trash2 size={16} /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
